import * as api from "api";
import { push } from "connected-react-router";
import { v4 as uuidv4 } from "uuid";
import ReactGA from "react-ga4";
import appActionTypes from "containers/App/_actions";
import { fetchFarm } from "containers/Farm/_actions";
import { fetchBudget } from "containers/Budget/_actions";
import { clearBaseAnalysis } from "containers/BudgetHome/Overview/_actions";

export const publishAnalysis = (publication) => (dispatch) => {
    const { farmId, budgetId } = publication;
    return new Promise((resolve, reject) => {
        ReactGA.event({
            category: "Budget",
            action: "Publish",
        });
        dispatch(
            api.put({
                path: `farms/${farmId}/budgets/${budgetId}/publications`,
                content: publication,
                onSuccess: () => {
                    resolve();
                    return (dispatch) => {
                        dispatch(fetchFarm(farmId, true));
                        dispatch(fetchBudget(farmId, budgetId));
                    };
                },
                onFailure: (error) => {
                    error.handled = true;
                    if (error.status === 426) {
                        reject("Publication modified by another user. Please refresh your browser and try again.");
                    } else if (error.status === 403) {
                        reject("There was an unexpected issue with your permissions. Please log out and back in and try again.");
                    } else {
                        reject("An unexpected error has occured. Please try again or call support if the problem persists.");
                    }
                    return [];
                },
            })
        );
    });
};

export const updatePublication = (publication) => (dispatch) => {
    return new Promise((resolve, reject) => {
        ReactGA.event({
            category: "Publications",
            action: "Update",
        });
        dispatch(
            api.put({
                path: `publications/${publication.id}`,
                content: publication,
                onSuccess: () => {
                    resolve();
                    return (dispatch) => {
                        dispatch(fetchFarm(publication.farmId, true));
                    };
                },
                onFailure: (error) => {
                    error.handled = true;
                    if (error.status === 426) {
                        reject("Publication modified by another user. Please refresh your browser and try again.");
                    } else if (error.status === 403) {
                        reject("There was an unexpected issue with your permissions. Please log out and back in and try again.");
                    } else {
                        reject("An unexpected error has occured. Please try again or call support if the problem persists.");
                    }
                    return [];
                },
            })
        );
    });
};

export const sharePublication = (invitation) => {
    ReactGA.event({
        category: "Publications",
        action: "Share",
    });
    return api.put({
        path: `publications/${invitation.publicationId}/agents`,
        content: invitation,
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
            };
        },
        onFailure: (error) => {
            error.handled = true;
            return (dispatch) => {
                switch (error.status) {
                    case 400:
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR_REPEATABLE, payload: "Error sending invite. Please ensure the email address is valid." });
                        break;
                    default:
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "An unexpected error has occured. Please try again or call support if the problem persists." });
                        break;
                }
            };
        },
    });
};

export const createWorkingCopy = (publication) => {
    ReactGA.event({
        category: "Publications",
        action: "CreateWorkingCopy",
    });
    const id = uuidv4();
    return api.post({
        path: `publications/${publication.id}/workingcopy/${id}`,
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch(fetchFarm(publication.farmId, true, id));
                dispatch(push(`/app/farm/${publication.farmId}/analysis/${id}`));
            };
        },
        onFailure: (error) => {
            error.handled = true;
            return (dispatch) => {
                if (error.status === 404) {
                    dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "Publication not found" });
                } else {
                    dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "An unexpected error has occured. Please try again or call support if the problem persists." });
                }
            };
        },
    });
};

export const deletePublication = (publication) => {
    ReactGA.event({
        category: "Publication",
        action: "Delete",
    });
    return api.del({
        path: `publications/${publication.id}`,
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch(clearBaseAnalysis(publication.id));
                dispatch(fetchFarm(publication.farmId, false, null));
                dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
            };
        },
        onFailure: (error) => {
            error.handled = true;
            return (dispatch) => {
                if (error.status === 404) {
                    dispatch(fetchFarm(publication.farmId));
                    dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
                } else if (error.status === 403) {
                    dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "You do not have permission to delete this publication." });
                } else if (error.offline) {
                    dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "Unable to delete this publication while offline. Please check your internet connection and try again." });
                } else {
                    dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "An unexpected error has occured. Please try again or call support if the problem persists." });
                }
            };
        },
    });
};

export const setAsPlan = (farmId, id) => {
    ReactGA.event({
        category: "Budget",
        action: "Set as plan",
    });

    return api.put({
        path: `publications/${id}/plan`,
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch(fetchFarm(farmId, true, undefined));
                dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
            };
        },
        onFailure: (error) => {
            error.handled = true;
            return (dispatch) => {
                dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "An unexpected error has occured. Please try again or call support if the problem persists." });
            };
        },
    });
};
